import type {
  TripContext,
  WatchAlertView,
} from "@b2bportal/air-price-watch-api";
import { useModuleBEM, useAirPriceWatchStyles } from "@b2bportal/core-themes";
import { CallState, CoreAirPriceWatchComponent } from "@b2bportal/core-types";
import { Loading } from "@b2bportal/core-ui";
import { useI18nContext } from "@hopper-b2b/i18n";
import { WatchCard } from "@tenant/air-price-watch";
import clsx from "clsx";
import defaultStyles from "./ChangeWatchEmailModalContent.module.scss";
import { useChangeWatchEmailModalContent } from "./hooks";

export interface ChangeWatchEmailModalContentComponentProps {
  watch: WatchAlertView;
  tripContext: TripContext;
  onClose?: () => void;
  refetch: () => void;
}

const COMPONENT_KEY = CoreAirPriceWatchComponent.ChangeWatchEmailModalContent;

export const ChangeWatchEmailModalContent = (
  props: ChangeWatchEmailModalContentComponentProps
) => {
  const styles = useAirPriceWatchStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();
  const {
    context: { watch, tripContext, callState, email, isValidEmail },
    handlers: { onChangeWatchEmail, onTryAgain, onClose, refetch, setEmail },
  } = useChangeWatchEmailModalContent(props);

  return (
    <div className={clsx(block)}>
      <WatchCard
        watch={watch}
        tripContext={tripContext}
        showActions={false}
        refetch={refetch}
      />
      {callState === CallState.NotCalled ? (
        <>
          <div className={cn("change-watch-email-description")}>
            {t("watch.changeEmailMessage")}
          </div>
          <label htmlFor="email" className={cn("email-label")}>
            {t("email")}
          </label>
          <input
            className={cn("email-input")}
            id="email"
            type="text"
            placeholder={t("watch.emailAddress")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button
            onClick={onChangeWatchEmail}
            className={cn("primary-watch-action")}
            disabled={!isValidEmail}
          >
            {t("watch.flightWatchChangeEmailButtonLabel")}
          </button>
          <button onClick={onClose} className={cn("secondary-watch-action")}>
            {t("cancel")}
          </button>
        </>
      ) : callState === CallState.InProcess ? (
        <div className="loading">
          <Loading />
        </div>
      ) : callState === CallState.Success ? (
        <>
          <div className={cn("change-watch-description")}>
            {t("watch.changeEmailSuccessModalSubtitle", { email: email })}
          </div>
          <button
            onClick={() => {
              onClose?.();
              refetch();
            }}
            className={cn("primary-watch-action")}
          >
            {t("done")}
          </button>
        </>
      ) : (
        <>
          <div className={cn("change-watch-description")}>
            {t("error.title")}
          </div>
          <button onClick={onTryAgain} className={cn("primary-watch-action")}>
            {t("tryAgain")}
          </button>
        </>
      )}
    </div>
  );
};
